import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import {
  HeadingBlock,
  HeroTitle,
  Lead,
  Eyebrow,
} from "features/onboarding/guided/GuidedWelcome";
import { OnboardingStyles } from "features/onboarding/common/styles";
import React, { useContext, useEffect, useState } from "react";
import Tabs from "common/Tabs/Tabs";
import {
  MOBILE_SEARCH,
  ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH,
} from "common/constants";
import ConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectTelegram";
import ConnectDiscord from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectDiscord";
import useGetBasicDetailsFromUuid from "@shared/features/onboarding/hooks/useGetBasicDetailsFromUuid";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import styled, { css } from "styled-components";
import { Discord, Telegram } from "common/Icons/Icons";
import Flex from "common/Boxes/Flex";
import {
  ExternalAccountType,
  OnboardingDetailedStep,
} from "@shared/features/onboarding/types";
import useMedia from "utils/hooks/useMedia";
import useConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/useConnectTelegram";
import ValidateConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ValidateConnectTelegram";
import Modal from "common/Modal/Modal";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { OnboardingStep } from "analytics/events";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { LanguageNameSpaces, UserSource } from "@shared/types";
import { getOnboardingUserSource, openMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const ConnectExternalAccount = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const { changeStep } = useContext(OnboardingContext)!;
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { queryCode, queryUuid } = useConnectTelegram();
  const [uuid, setUuid] = useState<string>(() => queryUuid || "");
  const shouldFetchUuid = Boolean(
    useRoutesMatch([ONBOARDING_CONNECT_EXTERNAL_ACCOUNT_PATH])
  );
  const getBasicDetailsFromUuidData = useGetBasicDetailsFromUuid(
    openMessage,
    uuid,
    !shouldFetchUuid
  );

  const userSource = getOnboardingUserSource();

  const initialTab =
    userSource === UserSource.DISCORD
      ? ExternalAccountType.DISCORD
      : ExternalAccountType.TELEGRAM;

  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [validateTelegramModalVisible, setValidateTelegramModalVisible] =
    useState(false);

  const track = useOnboardingAnalytics();
  useOnboardingStepView("connect_external_account");
  useEffect(() => {
    analytics?.connectExternalAccountView({
      onboardingStep: OnboardingStep.METHOD_SETTINGS,
    });
  }, [analytics]);

  const onConnectExternalAccount = (
    externalAccountType: ExternalAccountType
  ) => {
    analytics?.connectToExternalAccountClicked({
      onboardingStep: OnboardingStep.METHOD_SETTINGS,
      externalAccountType: externalAccountType,
    });
  };

  useEffect(() => {
    if (queryCode.length) setValidateTelegramModalVisible(true);
  }, [queryCode]);

  const isSelectedTab = (tab: ExternalAccountType) => {
    return selectedTab === tab;
  };

  const getTabDisplayName = (
    externalAccount: ExternalAccountType,
    icon: React.JSX.Element
  ) => {
    return (
      <TabWrapper $isSelected={isSelectedTab(externalAccount)}>
        {externalAccount}
        {icon}
      </TabWrapper>
    );
  };

  const onContinue = () => {
    changeStep(OnboardingDetailedStep.FOLLOW_SIGNALS_GROUP, {
      channelTab: "my",
      guided: "0",
    });
  };

  const telegramTabName = getTabDisplayName(
    ExternalAccountType.TELEGRAM,
    <StyledTelegram />
  );
  const discordTabName = getTabDisplayName(
    ExternalAccountType.DISCORD,
    <StyledDiscord />
  );

  const tabsItems = [
    {
      key: ExternalAccountType.TELEGRAM,
      label: telegramTabName,
      children: (
        <ConnectTelegram
          uuid={uuid}
          setUuid={setUuid}
          getBasicDetailsFromUuidData={getBasicDetailsFromUuidData}
          onContinueClick={() => {
            setValidateTelegramModalVisible(true);
            onConnectExternalAccount(ExternalAccountType.TELEGRAM);
          }}
        />
      ),
    },
    {
      key: ExternalAccountType.DISCORD,
      label: discordTabName,
      children: (
        <ConnectDiscord
          onSuccess={() => {
            track("External Account Connected", {
              external_platform: "discord",
            });
            onContinue();
            onConnectExternalAccount(ExternalAccountType.DISCORD);
          }}
        />
      ),
    },
  ];
  const tabs = (
    <StyledTabs
      activeKey={selectedTab}
      onChange={(selectedTab) =>
        setSelectedTab(selectedTab as ExternalAccountType)
      }
      $isMobile={isMobile}
      items={tabsItems}
    />
  );

  const validateTelegramModal = (
    <Modal
      open={validateTelegramModalVisible}
      onCancel={() => setValidateTelegramModalVisible(false)}
      footer={false}
      width={400}
      styles={{
        body: {
          padding: isMobile ? 16 : 32,
        },
      }}
      destroyOnHidden={true}
    >
      <ValidateConnectTelegram
        uuid={uuid}
        setUuid={setUuid}
        getBasicDetailsFromUuidData={getBasicDetailsFromUuidData}
        onSuccess={() => {
          track("External Account Connected", {
            external_platform: "telegram",
          });
          setValidateTelegramModalVisible(false);
          onContinue();
        }}
        code={queryCode}
        onCancel={() => setValidateTelegramModalVisible(false)}
        inModal={true}
      />
    </Modal>
  );

  return (
    <OnboardingStyles.StepWrapper
      style={{ width: "100%", maxWidth: isMobile ? 450 : 950 }}
    >
      <HeadingBlock>
        <Eyebrow>{t("intent.my_channels")}</Eyebrow>
        <HeroTitle>{t("revision.connect_channel")}</HeroTitle>
        <Lead>{t("revision.social_description")}</Lead>
      </HeadingBlock>
      <ConnectionCard>{tabs}</ConnectionCard>
      {validateTelegramModal}
    </OnboardingStyles.StepWrapper>
  );
};

export default ConnectExternalAccount;

const StyledTabs = styled(Tabs)<{ $isMobile: boolean }>`
  &.ant-tabs > .ant-tabs-nav {
    width: fit-content;
  }

  .ant-tabs-tab {
    padding: 4px 0 12px 0;
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0 0 0 56px;
  }

  &.ant-tabs-top > .ant-tabs-nav {
    margin: 0 0 42px;
  }

  ${(p) =>
    p.$isMobile &&
    `
  display: flex;
  justify-content: center;
  align-items: center;
  
   .ant-tabs-content-holder {
    width: 100%;
      }
  `}
`;
const TabWrapper = styled(Flex)<{ $isSelected: boolean }>`
  fill: ${(p) => p.theme.colors.textColor};
  ${(p) =>
    p.$isSelected &&
    `
    fill: ${p.theme.colors.primaryBlue};
    color: ${p.theme.colors.primaryBlue};
    `};

  &:hover {
    fill: ${(p) => p.theme.colors.primaryBlue};
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const iconStyles = css`
  margin-left: 12px;
  width: 20px;
`;
const StyledTelegram = styled(Telegram)`
  ${iconStyles}
`;
const StyledDiscord = styled(Discord)`
  ${iconStyles}
`;

const ConnectionCard = styled.div`
  width: 100%;
  max-width: 620px;
  padding: 28px;
  border: ${(p) => p.theme.borders.light};
  border-radius: 12px;
  background: ${(p) => p.theme.colors.cardBgColor};
  @media (max-width: 650px) {
    padding: 20px;
  }
`;
